import { mixins, Options } from "vue-class-component"
import { Team } from "project-simple-server-open-api/client"
import { Action, Getter } from "s-vuex-class"
import { v4 as uuidv4 } from "uuid"

import {
  Epic,
  FilterEpic,
  FilterIdea,
  FilterUser,
  Item,
  ItemInfo,
  MilestoneSearch,
  SearchIdeaResult,
  SearchMilestoneResult,
  SearchSprintResult,
  ServerResponse,
  User
} from "@/models"
import { FilterSprint } from "@/models/Filter/FilterSprint"
import { renderMilestoneName } from "@/models/Milestone"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { Actions, Getters } from "@/store"
import { parsingProjectIdAndItemNumber } from "@/utils/helpers"

import ReferenceComponentCommon from "../helpers/ReferenceComponentCommon"

@Options({
  name: "ParseReference"
})
export default class extends mixins(ReferenceComponentCommon) {
  @Getter(Getters.GET_SESSION_TEAM_ID) readonly teamId: string

  @Action(Actions.GET_USERS) findUsers: (params: FilterUser) => Promise<ServerResponse<User>>
  @Action(Actions.SEARCH_ITEM_WITH_TASKS) findTasks: (payload: {
    sequenceNumber: string
    projectName: string
  }) => Promise<Item>
  @Action(Actions.SEARCH_EPIC) findEpics: (filter: FilterEpic) => Promise<ServerResponse<Epic>>
  @Action(Actions.SEARCH_IDEA) findIdeas: (
    filter: FilterIdea
  ) => Promise<ServerResponse<SearchIdeaResult>>
  @Action(Actions.SEARCH_MILESTONE) findMilestones: (
    filter: MilestoneSearch
  ) => Promise<ServerResponse<SearchMilestoneResult>>
  @Action(Actions.SEARCH_SPRINT) findSprint: ({
    filter,
    onlyGet
  }: {
    filter: FilterSprint
    onlyGet?: boolean
  }) => Promise<ServerResponse<SearchSprintResult>>
  @Action(Actions.RESOLVE_SESSION_TEAM_ID) getTeamIdByUrlName: ({
    name,
    onlyGet
  }: {
    name: string
    onlyGet?: boolean
  }) => Promise<string>
  @Action(Actions.RESOLVE_SESSION_PROJECT_ID) getProjectIdByUrlName: ({
    name,
    onlyGet
  }: {
    name: string
    onlyGet?: boolean
  }) => Promise<string>
  @Action(Actions.RESOLVE_ITEM_ID) getItemIdByUrlName: ({
    sequenceNumber,
    onlyGet
  }: {
    sequenceNumber: number | string
    onlyGet?: boolean
  }) => Promise<string>
  @Action(Actions.GET_TEAM) getTeam: ({
    teamId,
    onlyGet
  }: {
    teamId: string
    onlyGet?: boolean
  }) => Promise<Team>
  @Action(Actions.GET_ITEM) getItem: ({
    itemId,
    onlyGet
  }: {
    itemId: string
    onlyGet?: boolean
  }) => Promise<Item>

  mounted() {
    if (this.node?.attrs?.loading) {
      if (this.type === ReferenceType.PROJECT) {
        this.searchProject()
      }
      if (this.type === ReferenceType.USER) {
        this.searchUser()
      }
      if (this.type === ReferenceType.PREVIOUS_TASK_OWNER) {
        this.searchUser()
      }
      if (this.type === ReferenceType.ITEM) {
        this.searchItem()
      }
      if (this.type === ReferenceType.TASK) {
        this.searchTask()
      }
      if (this.type === ReferenceType.SPRINT) {
        this.searchSprint()
      }
      if (this.type === ReferenceType.MILESTONE) {
        this.searchMilestone()
      }
      if (this.type === ReferenceType.EPIC) {
        this.searchEpic()
      }
      if (this.type === ReferenceType.IDEA) {
        this.searchIdea()
      }
      if (this.type === ReferenceType.TEAM) {
        this.searchTeam()
      }
    }
  }

  async searchUser() {
    const userName = this.text.toLowerCase().substring(1)
    const filter = FilterUser.createDefault()
    filter.text = userName
    const serverResponseUser = await this.findUsers(filter)
    const users = serverResponseUser.content
    if (users.length) {
      const user = users.find(user => user.username.toLowerCase() === userName)
      if (user) {
        this.safeUpdateAttributes({
          id: user.id,
          info: JSON.stringify(user),
          linkText: `@${user.fullName}`,
          linkHref: await this.getHrefByType(ReferenceType.USER, user),
          loading: false
        })
      } else {
        this.setDefaultText()
      }
    }
  }

  async searchItem() {
    try {
      let sequenceNumber
      if (this.route.params.itemId) {
        const parsingResult = parsingProjectIdAndItemNumber(
          this.route.params.projectId + "-" + this.route.params.itemId
        )
        sequenceNumber = parsingResult.itemId
      } else if (this.route.query.i) {
        const queryItem = Array.isArray(this.route.query.i)
          ? this.route.query.i[0]
          : this.route.query.i
        const queryString = queryItem?.toString() || ""

        if (queryString.includes("-")) {
          const parsingResult = parsingProjectIdAndItemNumber(queryString)
          sequenceNumber = parsingResult.itemId
        } else {
          sequenceNumber = queryString.match(/\d+$/)?.[0] || queryString
        }
      }
      if (this.text.includes("#")) {
        sequenceNumber = this.text.split("#")[1]
      }
      const itemId = await this.getItemIdByUrlName({
        sequenceNumber,
        onlyGet: true
      })
      const result = await this.getItem({
        itemId,
        onlyGet: true
      })
      const itemInfo = new ItemInfo({
        ...result
      }) // this necessary to remove the redundant fields
      if (itemInfo) {
        this.safeUpdateAttributes({
          id: itemInfo.id,
          info: JSON.stringify(itemInfo),
          linkText: `#${itemInfo.sequenceNumber} - ${itemInfo.title}`,
          linkHref: await this.getHrefByType(ReferenceType.ITEM, itemInfo),
          loading: false
        })
      } else {
        this.setDefaultText()
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  async searchTask() {
    try {
      const parsingResult = parsingProjectIdAndItemNumber(
        this.route.params.projectId + "-" + this.route.params.itemId
      )
      const sequenceNumber = parsingResult.itemId
      const taskNumber = this.route.query.task as string
      const taskId = this.route.query.taskId as string
      const projectName = this.route.params.projectId as string

      const itemResult = await this.findTasks({ sequenceNumber, projectName })
      if (itemResult) {
        const task = taskId
          ? itemResult.tasks.find(task => task.id === taskId)
          : itemResult.tasks[taskNumber]
        if (task) {
          const info = { ...task, parentSequenceNumber: sequenceNumber }
          this.safeUpdateAttributes({
            id: task.id,
            info: JSON.stringify(info),
            linkText: `#${itemResult.sequenceNumber}.${Number(task.order) + 1} - ${task.name}`,
            linkHref: await this.getHrefByType(ReferenceType.TASK, info),
            loading: false
          })
        } else {
          this.setDefaultText()
        }
      } else {
        this.setDefaultText()
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  async searchProject() {
    try {
      let name = this.route.params.projectId as string
      if (this.text.includes("#")) {
        name = this.text.split("#P - ")[1]
      }
      name = decodeURI(name)

      const projectId = await this.getProjectIdByUrlName({
        name,
        onlyGet: true
      })
      const result = await this.getProject({
        projectId,
        onlyGet: true
      })

      if (result) {
        const info = { id: result.id, urlName: result.urlName }
        this.safeUpdateAttributes({
          id: result.id,
          info: JSON.stringify(info),
          linkText: `#P - ${result.name}`,
          linkHref: await this.getHrefByType(ReferenceType.PROJECT, info),
          loading: false
        })
      } else {
        this.setDefaultText()
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  async searchSprint() {
    try {
      let sequenceNumber = this.route.params.sprintId as string
      const teamName = this.route.params.teamId as string
      const projectUrlName = this.route.params.projectId as string

      if (this.text.includes("#")) {
        sequenceNumber = this.text.split("#S - ")[1]
      } else {
        const projectId = await this.getProjectIdByUrlName({
          name: projectUrlName,
          onlyGet: true
        })

        const sprint = await this.findSprintWithPagination(teamName, projectId, sequenceNumber)

        if (sprint) {
          await this.updateSprintAttributes(sprint, teamName, sequenceNumber)
        } else {
          this.setDefaultText()
        }
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  async findSprintWithPagination(
    teamName: string,
    projectId: string,
    sequenceNumber: string
  ): Promise<SearchSprintResult | null> {
    const filter = FilterSprint.createDefault()
    filter.teamName = teamName
    filter.projectId = projectId

    let page = 1
    let hasMorePages = true

    while (hasMorePages) {
      filter.page = page
      const response = await this.findSprint({ filter, onlyGet: true })

      const found =
        sequenceNumber === "active"
          ? response.content.find(sprint => sprint.status === "STARTED")
          : response.content.find(sprint => sprint.sequenceNumber.toString() === sequenceNumber)

      if (found) return found

      hasMorePages = !response.last
      page++
    }

    return null
  }

  async updateSprintAttributes(
    sprint: SearchSprintResult,
    teamName: string,
    sequenceNumber: string
  ) {
    const teamId = await this.getTeamIdByUrlName({
      name: teamName,
      onlyGet: true
    })
    const team = await this.getTeam({
      teamId,
      onlyGet: true
    })

    const sprintWithTeam = { ...sprint, team }
    const sprintUrl =
      sequenceNumber === "active"
        ? this.originUrl.replace("/active", `/${sprint.sequenceNumber}`)
        : this.originUrl

    this.safeUpdateAttributes({
      id: sprint.id,
      info: JSON.stringify(sprintWithTeam),
      linkText: `Sprint #${sprint.sequenceNumber}`,
      linkHref: sprintUrl,
      loading: false
    })
  }

  async searchMilestone() {
    try {
      const projectName = this.route.params.projectId as string
      let milestoneId = (this.route.params.milestoneId || this.route.params.index || "a") as string

      if (this.text.includes("#")) {
        this.safeUpdateAttributes({ loading: false, info: "{}" })
        milestoneId = this.text.split("#M - ")[1]
      } else {
        try {
          milestoneId = decodeURIComponent(milestoneId).replace(/\/$/, "")
        } catch {
          // If decoding fails, fall back to the raw milestone string
          milestoneId = milestoneId.replace(/\/$/, "")
        }

        const serverResponse = await this.findMilestones(
          new MilestoneSearch({
            q: milestoneId,
            projectName
          })
        )
        let milestoneResults = serverResponse.content

        if (!milestoneResults?.length && milestoneId.toLowerCase().startsWith("milestone ")) {
          const milestoneKey = milestoneId.replace(/^milestone\s+/i, "")
          const fallbackResponse = await this.findMilestones(
            new MilestoneSearch({
              q: milestoneKey,
              projectName
            })
          )
          milestoneResults = fallbackResponse.content
        }

        if (milestoneResults?.length) {
          const normalizedMilestoneId = renderMilestoneName(milestoneId)
          const result = milestoneResults.find(result => {
            const title = result.title.toLowerCase()
            const titleParsed = result.titleParsed.toLowerCase()
            const milestoneLower = milestoneId.toLowerCase()
            const normalizedLower = normalizedMilestoneId.toLowerCase()
            return (
              title === milestoneLower ||
              titleParsed === milestoneLower ||
              title === normalizedLower ||
              titleParsed === normalizedLower
            )
          })

          if (result) {
            const info = { ...result, projectName }
            this.safeUpdateAttributes({
              id: result.id,
              info: JSON.stringify(info),
              linkText: `#M - ${result.titleParsed}`,
              linkHref: await this.getHrefByType(ReferenceType.MILESTONE, info),
              loading: false
            })
          } else {
            if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
              return
            }
            this.setDefaultText()
          }
        } else {
          if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
            return
          }
          this.setDefaultText()
        }
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  async searchEpic() {
    try {
      let epicNumber = this.route.params.epicId as string
      const projectName = this.route.params.projectId as string
      if (this.text.includes("#")) {
        this.safeUpdateAttributes({ loading: false, info: "{}" })
        epicNumber = this.text.split("#E - ")[1]
      } else {
        const serverResponse = await this.findEpics(new FilterEpic({ q: epicNumber, projectName }))
        const epicResults = serverResponse.content
        if (epicResults?.length) {
          const result = epicResults.find(result => result.sequenceNumber.toString() === epicNumber)
          if (result) {
            const info = { ...result, projectName }
            this.safeUpdateAttributes({
              id: result.id,
              info: JSON.stringify(info),
              linkText: `#E - ${result.sequenceNumber} ${result.name}`,
              linkHref: await this.getHrefByType(ReferenceType.EPIC, info),
              loading: false
            })
          } else {
            if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
              return
            }
            this.setDefaultText()
          }
        } else {
          if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
            return
          }
          this.setDefaultText()
        }
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  async searchIdea() {
    const pastedNumber = this.text.split("#I - ")[1]
    const sequenceNumber = (pastedNumber ?? this.route.query.idea) as string
    const projectName = pastedNumber ? "" : (this.route.params.projectId as string)
    const filter = new FilterIdea({
      q: sequenceNumber,
      projectName,
      projectIds: projectName ? [] : [this.projectId]
    })
    filter.addSort("sequenceNumber", true)

    const serverResponse = await this.findIdeas(filter)
    const result = serverResponse?.content?.find(
      idea => idea.sequenceNumber.toString() === sequenceNumber
    )
    if (result) {
      this.safeUpdateAttributes({
        id: result.id,
        info: JSON.stringify(result),
        linkText: `#I - ${result.sequenceNumber} ${result.title}`,
        linkHref: await this.getHrefByType(ReferenceType.IDEA, result),
        loading: false
      })
      return
    }

    if (!this.originUrl) {
      this.safeUpdateAttributes({ loading: false, info: "{}" })
      return
    }
    if (!this.replaceWithPlainLink(this.originUrl)) {
      this.setDefaultText()
    }
  }

  async searchTeam() {
    try {
      let teamName = this.route.params.teamId as string
      if (this.text.includes("#")) {
        teamName = this.text.split("#T - ")[1]
      }

      teamName = decodeURI(teamName)
      const teamId = await this.getTeamIdByUrlName({
        name: teamName,
        onlyGet: true
      })
      const team = await this.getTeam({
        teamId,
        onlyGet: true
      })
      if (team) {
        this.safeUpdateAttributes({
          id: team.id,
          info: JSON.stringify(team),
          linkText: `#T - ${team.name}`,
          linkHref: await this.getHrefByType(ReferenceType.TEAM, team),
          loading: false
        })
      } else {
        if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
          return
        }
        this.setDefaultText()
      }
    } catch (error) {
      if (this.originUrl && this.replaceWithPlainLink(this.originUrl)) {
        return
      }
      this.setDefaultText()
    }
  }

  setDefaultText() {
    this.safeUpdateAttributes({
      id: uuidv4(),
      loading: false,
      info: "{}",
      linkText: `${this.originUrl}`,
      linkHref: this.originUrl
    })
  }
}
